import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { createSchoolUser, listAllUsers, listPendingInvites, sendPasswordResetToUser, importStudentAccountsBatch, deleteInvite } from '../api/adminApi';
import { saveFee, listAllFees, deleteFee } from '../api/feesApi';
import { listAllStudents } from '../api/studentsApi';
import { AppHeader } from './LoginPanel';
import SearchableSelect from './SearchableSelect';
import { matchesSearch } from '../utils/searchFilter';
import { parseStudentLoginsCsv, chunkArray } from '../utils/parseStudentLoginsCsv';
import { clearStudentRegistryCache } from '../api/studentsApi';
import AdminAcademicsTab from './AdminAcademicsTab';
import AttendanceSummarySection from './AttendanceSummarySection';
import CoordinatorPanel from './CoordinatorPanel';

const ROLES = ['TEACHER', 'PARENT', 'COORDINATOR', 'SUPERUSER'];

function RoleBadge({ role }) {
  const cls =
    role === 'SUPERUSER'
      ? 'admin'
      : role === 'COORDINATOR'
        ? 'coordinator'
        : role === 'TEACHER'
          ? 'teacher'
          : role === 'STUDENT'
            ? 'student'
            : 'parent';
  return <span className={`mps-badge mps-badge-${cls}`}>{role}</span>;
}

export default function AdminPanel() {
  const { user, profile } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [fees, setFees] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const [form, setForm] = useState({ email: '', name: '', role: 'TEACHER', sendReset: true });
  const [feeForm, setFeeForm] = useState({ studentId: '', studentName: '', parentUid: '', amountDue: '', monthsPaid: '' });
  const [usersFilter, setUsersFilter] = useState('');
  const [invitesFilter, setInvitesFilter] = useState('');
  const [feesFilter, setFeesFilter] = useState('');
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState('');

  const roleOpts = useMemo(() => ROLES.map((r) => ({ value: r, label: r })), []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [u, f, inv, st] = await Promise.all([listAllUsers(), listAllFees(), listPendingInvites(), listAllStudents()]);
      setUsers(u.sort((a, b) => (a.email || '').localeCompare(b.email || '')));
      setFees(f);
      setInvites(inv);
      setStudents(st);
    } catch (e) {
      setErr(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setMsg('');
    setErr('');
    try {
      const result = await createSchoolUser({
        email: form.email,
        name: form.name,
        role: form.role,
      });
      if (result.method === 'function') {
        if (form.sendReset) {
          await sendPasswordResetToUser(form.email);
          setMsg(`User ${result.email} created. Password reset email sent.`);
        } else {
          setMsg(`User ${result.email} created in Firebase Auth.`);
        }
      } else {
        setMsg(
          `Invitation saved for ${result.email}. Ask them to open ${window.location.origin} → Register (invited) and set their password.`
        );
      }
      setForm({ email: '', name: '', role: 'TEACHER', sendReset: true });
      await load();
    } catch (e) {
      setErr(e.message || 'Could not create user');
    }
  };

  const handleReset = async (email) => {
    setMsg('');
    setErr('');
    try {
      await sendPasswordResetToUser(email);
      setMsg(`Password reset email sent to ${email}`);
    } catch (e) {
      setErr(e.message || 'Reset failed');
    }
  };

  const handleSaveFee = async (e) => {
    e.preventDefault();
    setMsg('');
    setErr('');
    if (!feeForm.studentId.trim()) {
      setErr('Please select or enter a student (use admission number for student login).');
      return;
    }
    try {
      await saveFee(feeForm);
      setMsg(`Fee record saved for ${feeForm.studentId}`);
      setFeeForm({ studentId: '', studentName: '', parentUid: '', amountDue: '', monthsPaid: '' });
      await load();
    } catch (e) {
      setErr(e.message || 'Could not save fee');
    }
  };

  const handleImportStudentLogins = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setMsg('');
    setErr('');
    setImporting(true);
    setImportProgress('Reading file…');
    try {
      const text = await file.text();
      const rows = parseStudentLoginsCsv(text);
      if (rows.length === 0) {
        throw new Error('No valid rows in CSV. Use student_logins_2026.csv format.');
      }
      const batches = chunkArray(rows, 20);
      let totalCreated = 0;
      let totalUpdated = 0;
      let totalSkipped = 0;
      const allErrors = [];
      for (let i = 0; i < batches.length; i += 1) {
        setImportProgress(`Importing batch ${i + 1} of ${batches.length}…`);
        const result = await importStudentAccountsBatch(batches[i]);
        totalCreated += result.created || 0;
        totalUpdated += result.updated || 0;
        totalSkipped += result.skipped || 0;
        if (result.errors?.length) allErrors.push(...result.errors);
      }
      clearStudentRegistryCache();
      await load();
      const errNote = allErrors.length ? ` ${allErrors.length} row(s) had errors.` : '';
      setMsg(
        `Student login import done: ${totalCreated} created, ${totalUpdated} updated, ${totalSkipped} skipped.${errNote} Students can sign in with login_id + password from the CSV.`
      );
      if (allErrors.length) {
        console.warn('Import errors', allErrors);
      }
    } catch (ex) {
      setErr(ex.message || 'Import failed. Deploy Cloud Functions (importStudentAccounts) and sign in as admin.');
    } finally {
      setImporting(false);
      setImportProgress('');
    }
  };

  const parents = users.filter((u) => u.role === 'PARENT');
  const parentOpts = useMemo(
    () => parents.map((p) => ({ value: p.id, label: `${p.name || '—'} (${p.email || p.id})` })),
    [parents]
  );
  const studentOpts = useMemo(
    () =>
      students.map((s) => ({
        value: s.studentId,
        label: `${s.studentName} · adm ${s.admno} · class ${s.class || '—'}`,
      })),
    [students]
  );
  const feeStudentPick = students.some((s) => s.studentId === feeForm.studentId) ? feeForm.studentId : '';

  const filteredUsers = useMemo(
    () => users.filter((u) => matchesSearch(usersFilter, [u.name, u.email, u.role, u.id])),
    [users, usersFilter]
  );
  const filteredInvites = useMemo(
    () => invites.filter((inv) => matchesSearch(invitesFilter, [inv.email, inv.name, inv.role, inv.id])),
    [invites, invitesFilter]
  );
  const filteredFees = useMemo(
    () =>
      fees.filter((f) =>
        matchesSearch(feesFilter, [f.studentName, f.id, String(f.amountDue ?? ''), (f.monthsPaid || []).join(', '), f.parentUid])
      ),
    [fees, feesFilter]
  );

  return (
    <div className="mps-app">
      <AppHeader title="Admin dashboard" user={user} profile={profile} onSignOut={() => auth && signOut(auth)} />
      <main className="mps-main">
        <div className="mps-tabs">
          <button type="button" className={`mps-tab ${tab === 'users' ? 'mps-tab-active' : ''}`} onClick={() => setTab('users')}>
            Users
          </button>
          <button type="button" className={`mps-tab ${tab === 'fees' ? 'mps-tab-active' : ''}`} onClick={() => setTab('fees')}>
            Dues / fees
          </button>
          <button type="button" className={`mps-tab ${tab === 'import' ? 'mps-tab-active' : ''}`} onClick={() => setTab('import')}>
            Student logins
          </button>
          <button type="button" className={`mps-tab ${tab === 'academics' ? 'mps-tab-active' : ''}`} onClick={() => setTab('academics')}>
            Academics
          </button>
          <button type="button" className={`mps-tab ${tab === 'coordinator' ? 'mps-tab-active' : ''}`} onClick={() => setTab('coordinator')}>
            Coordinator
          </button>
          <button type="button" className={`mps-tab ${tab === 'attendance' ? 'mps-tab-active' : ''}`} onClick={() => setTab('attendance')}>
            Attendance
          </button>
        </div>

        {err ? <div className="mps-msg mps-msg-error">{err}</div> : null}
        {msg ? <div className="mps-msg mps-msg-success">{msg}</div> : null}

        {tab === 'users' ? (
          <>
            <div className="mps-card">
              <h2>Add user (Teacher / Parent / Admin)</h2>
              <form onSubmit={handleCreateUser}>
                <div className="mps-grid">
                  <div className="mps-field">
                    <label>Full name</label>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                  </div>
                  <div className="mps-field">
                    <label>Email</label>
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                  </div>
                  <div className="mps-field">
                    <label>Role</label>
                    <SearchableSelect
                      value={form.role}
                      onChange={(v) => setForm({ ...form, role: v })}
                      options={roleOpts}
                      placeholder="Search role…"
                    />
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0', fontSize: '0.85rem' }}>
                  <input type="checkbox" checked={form.sendReset} onChange={(e) => setForm({ ...form, sendReset: e.target.checked })} />
                  Send password reset email so they can set their password
                </label>
                <button type="submit" className="mps-btn">
                  Create user
                </button>
              </form>
            </div>

            <div className="mps-card">
              <h2>All users ({users.length})</h2>
              {loading ? (
                <p>Loading…</p>
              ) : (
                <>
                  <input
                    type="search"
                    className="mps-table-filter"
                    value={usersFilter}
                    onChange={(e) => setUsersFilter(e.target.value)}
                    placeholder="Search name, email, role, UID…"
                    aria-label="Filter users"
                  />
                  <table className="mps-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>UID</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.id}>
                        <td>{u.name || '—'}</td>
                        <td>{u.email}</td>
                        <td>
                          <RoleBadge role={u.role} />
                        </td>
                        <td style={{ fontSize: '0.7rem', wordBreak: 'break-all' }}>{u.id}</td>
                        <td>
                          <button type="button" className="mps-btn mps-btn-sm mps-btn-secondary" onClick={() => handleReset(u.email)}>
                            Send reset link
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </>
              )}
            </div>
            <div className="mps-card">
              <h2>Pending invitations ({invites.length})</h2>
              {invites.length === 0 ? (
                <p className="mps-msg mps-msg-info">No pending invites.</p>
              ) : (
                <>
                  <input
                    type="search"
                    className="mps-table-filter"
                    value={invitesFilter}
                    onChange={(e) => setInvitesFilter(e.target.value)}
                    placeholder="Search email, name, role…"
                    aria-label="Filter invitations"
                  />
                  <table className="mps-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvites.map((inv) => (
                      <tr key={inv.id}>
                        <td>{inv.email}</td>
                        <td>{inv.name}</td>
                        <td>
                          <RoleBadge role={inv.role} />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="mps-btn mps-btn-sm mps-btn-danger"
                            onClick={async () => {
                              if (!window.confirm(`Delete invite for ${inv.email}?`)) return;
                              setMsg('');
                              setErr('');
                              try {
                                await deleteInvite(inv.email);
                                setMsg(`Invite deleted: ${inv.email}`);
                                await load();
                              } catch (e) {
                                setErr(e.message || 'Could not delete invite');
                              }
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </>
              )}
            </div>
          </>
        ) : tab === 'fees' ? (
          <>
            <div className="mps-card">
              <h2>Add / update dues record</h2>
              <p className="mps-muted" style={{ marginTop: 0 }}>
                Use the student&apos;s <strong>admission number</strong> as Student ID so they see dues after login. Parent is optional.
              </p>
              <form onSubmit={handleSaveFee}>
                {studentOpts.length > 0 ? (
                  <div className="mps-field" style={{ marginBottom: 12 }}>
                    <label>Pick student (search)</label>
                    <SearchableSelect
                      value={feeStudentPick}
                      onChange={(id) => {
                        if (!id) {
                          setFeeForm((f) => ({ ...f, studentId: '', studentName: '' }));
                          return;
                        }
                        const s = students.find((x) => x.studentId === id);
                        if (s) {
                          setFeeForm((f) => ({
                            ...f,
                            studentId: s.studentId,
                            studentName: s.studentName,
                          }));
                        }
                      }}
                      options={studentOpts}
                      emptyOption={{ value: '', label: '— Clear selection —' }}
                      placeholder="Search by name, adm no, class…"
                    />
                  </div>
                ) : (
                  <p className="mps-msg mps-msg-info" style={{ marginBottom: 12 }}>
                    No students in registry yet. Run the CSV import script, or enter admission number manually below.
                  </p>
                )}
                <div className="mps-grid">
                  <div className="mps-field">
                    <label>Student ID (admission no.)</label>
                    <input value={feeForm.studentId} onChange={(e) => setFeeForm({ ...feeForm, studentId: e.target.value })} required />
                  </div>
                  <div className="mps-field">
                    <label>Student name</label>
                    <input value={feeForm.studentName} onChange={(e) => setFeeForm({ ...feeForm, studentName: e.target.value })} required />
                  </div>
                  <div className="mps-field">
                    <label>Parent (optional)</label>
                    <SearchableSelect
                      value={feeForm.parentUid}
                      onChange={(v) => setFeeForm({ ...feeForm, parentUid: v })}
                      options={parentOpts}
                      emptyOption={{ value: '', label: '— No parent link —' }}
                      placeholder="Search parent by name or email…"
                    />
                  </div>
                  <div className="mps-field">
                    <label>Amount due (₹)</label>
                    <input type="number" value={feeForm.amountDue} onChange={(e) => setFeeForm({ ...feeForm, amountDue: e.target.value })} required />
                  </div>
                  <div className="mps-field">
                    <label>Months paid (comma separated)</label>
                    <input placeholder="Apr-2026, May-2026" value={feeForm.monthsPaid} onChange={(e) => setFeeForm({ ...feeForm, monthsPaid: e.target.value })} />
                  </div>
                </div>
                <button type="submit" className="mps-btn" style={{ marginTop: 12 }}>
                  Save fee
                </button>
              </form>
            </div>
            <div className="mps-card">
              <h2>Fee records</h2>
              <input
                type="search"
                className="mps-table-filter"
                value={feesFilter}
                onChange={(e) => setFeesFilter(e.target.value)}
                placeholder="Search student, amount, months, parent UID…"
                aria-label="Filter fee records"
              />
              <table className="mps-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Amount due</th>
                    <th>Months paid</th>
                    <th>Parent UID</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFees.map((f) => (
                    <tr key={f.id}>
                      <td>
                        {f.studentName || f.id} ({f.id})
                      </td>
                      <td>₹{f.amountDue}</td>
                      <td>{(f.monthsPaid || []).join(', ') || '—'}</td>
                      <td style={{ fontSize: '0.7rem' }}>{f.parentUid}</td>
                      <td>
                        <button
                          type="button"
                          className="mps-btn mps-btn-sm mps-btn-danger"
                          onClick={async () => {
                            if (!window.confirm(`Delete fee record for ${f.studentName || f.id}?`)) return;
                            setMsg('');
                            setErr('');
                            try {
                              await deleteFee(f.id);
                              setMsg(`Fee record deleted for ${f.id}`);
                              await load();
                            } catch (e) {
                              setErr(e.message || 'Could not delete fee');
                            }
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : tab === 'academics' ? (
          <AdminAcademicsTab setMsg={setMsg} setErr={setErr} />
        ) : tab === 'coordinator' ? (
          <CoordinatorPanel embedded adminMode />
        ) : tab === 'attendance' ? (
          <AttendanceSummarySection setMsg={setMsg} setErr={setErr} allowDelete />
        ) : (
          <div className="mps-card">
            <h2>Create student login accounts</h2>
            <p className="mps-muted" style={{ marginTop: 0 }}>
              Upload <strong>student_logins_2026.csv</strong>. This creates Firebase sign-in accounts so students can log in with{' '}
              <code>login_id</code> + <code>password</code> (DOB). The CSV alone does not create accounts until you import here.
            </p>
            <p className="mps-msg mps-msg-info">
              Example: login <code>aditya1634</code>, password <code>13022012</code> (from CSV). Requires admin account and Cloud Functions.
            </p>
            <div className="mps-field">
              <label htmlFor="student-logins-csv">Student logins CSV</label>
              <input
                id="student-logins-csv"
                type="file"
                accept=".csv,text/csv"
                disabled={importing}
                onChange={handleImportStudentLogins}
              />
            </div>
            {importProgress ? <p className="mps-muted">{importProgress}</p> : null}
            <p className="mps-muted" style={{ fontSize: '0.85rem', marginTop: 12 }}>
              File path on your PC: <code>F:\school\SESSION 2026-27\student_logins_2026.csv</code>
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
